import { formatMoney } from '@/utils/format';
import { balanceAfterAdvance } from '@/utils/invoice';
import type { Invoice } from '@/types/portal';

export default function InvoiceBreakdown({ invoice }: { invoice: Invoice }) {
  const balance = balanceAfterAdvance(invoice.amount, invoice.advance);

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center justify-between gap-3 border-b border-background-200 px-5 py-4">
        <div>
          <h2 className="font-heading text-sm font-semibold text-foreground-950">
            Invoice breakdown
          </h2>
          <p className="mt-0.5 text-xs text-foreground-500">
            Line items raised against the contracted deliverable
          </p>
        </div>
        <span className="hidden h-8 w-8 items-center justify-center rounded-md bg-background-100 text-foreground-600 sm:flex">
          <i className="ri-file-list-3-line" />
        </span>
      </header>

      <div className="overflow-x-auto">
        <table className="row-accent w-full min-w-[520px] border-collapse text-left">
          <thead>
            <tr className="border-b border-background-100 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
              <th className="px-5 py-2.5 font-semibold">Description</th>
              <th className="px-5 py-2.5 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems.map((item) => (
              <tr key={item.id} className="border-b border-background-100 last:border-0">
                <td className="px-5 py-3">
                  <p className="text-sm font-medium text-foreground-900">{item.description}</p>
                  <p className="text-xs text-foreground-500">{item.detail}</p>
                </td>
                <td className="px-5 py-3 text-right text-sm text-foreground-800">
                  {formatMoney(item.amount, invoice.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-background-200 px-5 py-4">
        <dl className="ml-auto max-w-xs space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-foreground-500">Subtotal</dt>
            <dd className="text-foreground-800">
              {formatMoney(invoice.subtotal, invoice.currency)}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-foreground-500">Withholding tax</dt>
            <dd className="text-foreground-800">
              {invoice.tax > 0 ? formatMoney(invoice.tax, invoice.currency) : '—'}
            </dd>
          </div>
          <div className="flex items-center justify-between border-t border-background-100 pt-2">
            <dt className="font-medium text-foreground-700">Invoice total</dt>
            <dd className="text-foreground-900">
              {formatMoney(invoice.amount, invoice.currency)}
            </dd>
          </div>
          {invoice.advance ? (
            <>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">
                  Early payout advance ({invoice.advance.percent}%)
                </dt>
                <dd className="text-secondary-800">
                  − {formatMoney(invoice.advance.amount, invoice.currency)}
                </dd>
              </div>
              <div className="flex items-center justify-between border-t border-background-100 pt-2">
                <dt className="font-medium text-foreground-700">Balance payable</dt>
                <dd className="font-heading text-base font-semibold text-foreground-950">
                  {formatMoney(balance, invoice.currency)}
                </dd>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between border-t border-background-100 pt-2">
              <dt className="font-medium text-foreground-700">Total payable</dt>
              <dd className="font-heading text-base font-semibold text-foreground-950">
                {formatMoney(invoice.amount, invoice.currency)}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {invoice.notes ? (
        <div className="border-t border-background-200 px-5 py-4">
          <p className="text-[11px] font-label font-medium uppercase tracking-wider text-foreground-400">
            Creator note
          </p>
          <p className="mt-1 text-sm leading-relaxed text-foreground-700">{invoice.notes}</p>
        </div>
      ) : null}
    </section>
  );
}